import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { FieldProps } from '@rjsf/utils';
import { CustomFieldExtensionSchema } from '@backstage/plugin-scaffolder-react';
import {
  AGENT_LOGO_DROPZONE_BACKGROUND_DARK,
  AGENT_LOGO_DROPZONE_BACKGROUND_LIGHT,
  AGENT_LOGO_DROPZONE_BORDER_DARK,
  AGENT_LOGO_DROPZONE_BORDER_LIGHT,
} from '@mb.io/core-components';

type AgentLogoUploadUiOptions = {
  acceptedMimeTypes?: string[];
  maxFileSizeKb?: number;
  requiredWidth?: number;
  requiredHeight?: number;
};

const DEFAULT_ACCEPTED_MIME_TYPES = ['image/avif'];
const DEFAULT_LOGO_WIDTH = 60;
const DEFAULT_LOGO_HEIGHT = 60;
const DEFAULT_MAX_FILE_SIZE_KB = 50;

const toDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Failed to read selected file.'));
    reader.readAsDataURL(file);
  });

const getImageDimensions = (
  file: File,
): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const imageUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      resolve({ width: image.width, height: image.height });
      URL.revokeObjectURL(imageUrl);
    };
    image.onerror = () => {
      reject(new Error('Unable to read image dimensions.'));
      URL.revokeObjectURL(imageUrl);
    };
    image.src = imageUrl;
  });

const formatFileSizeKb = (size: number) =>
  `${Math.max(1, Math.round(size / 1024))} KB`;

const isPreviewableLogoValue = (value: unknown): value is string => {
  if (typeof value !== 'string') {
    return false;
  }

  return (
    value.startsWith('data:') ||
    value.startsWith('http://') ||
    value.startsWith('https://')
  );
};

export const AgentLogoUploadField = ({
  id,
  required,
  formData,
  onChange,
  rawErrors,
  schema,
  uiSchema,
}: FieldProps<string>) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState<string | undefined>(undefined);
  const [isPreviewBroken, setIsPreviewBroken] = useState(false);
  const [selectedFileSize, setSelectedFileSize] = useState<string | undefined>(
    undefined,
  );
  const [prefilledPreviewUrl, setPrefilledPreviewUrl] = useState<
    string | undefined
  >(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    try {
      const params = new URLSearchParams(window.location.search);
      const encodedFormData = params.get('formData');
      if (!encodedFormData) {
        return undefined;
      }

      const parsedFormData = JSON.parse(encodedFormData) as {
        logoPresignedUrl?: string;
        agent_logo_presigned_url?: string;
      };

      const url =
        parsedFormData.logoPresignedUrl?.trim() ||
        parsedFormData.agent_logo_presigned_url?.trim() ||
        undefined;

      return url && isPreviewableLogoValue(url) ? url : undefined;
    } catch {
      return undefined;
    }
  });

  const options = (uiSchema?.['ui:options'] ?? {}) as AgentLogoUploadUiOptions;
  const acceptedMimeTypes =
    options.acceptedMimeTypes && options.acceptedMimeTypes.length > 0
      ? options.acceptedMimeTypes
      : DEFAULT_ACCEPTED_MIME_TYPES;
  const requiredWidth =
    typeof options.requiredWidth === 'number'
      ? options.requiredWidth
      : DEFAULT_LOGO_WIDTH;
  const requiredHeight =
    typeof options.requiredHeight === 'number'
      ? options.requiredHeight
      : DEFAULT_LOGO_HEIGHT;
  const maxFileSizeKb =
    typeof options.maxFileSizeKb === 'number'
      ? options.maxFileSizeKb
      : DEFAULT_MAX_FILE_SIZE_KB;

  const effectiveError = localError ?? rawErrors?.[0];

  const validateAndSetFile = async (file?: File) => {
    setLocalError(undefined);

    if (!file) {
      return;
    }

    if (!acceptedMimeTypes.includes(file.type)) {
      setLocalError(
        `Error. Unsupported format. Upload an AVIF file sized ${requiredWidth}x${requiredHeight} px.`,
      );
      return;
    }

    if (file.size > maxFileSizeKb * 1024) {
      setLocalError(
        `Error. File is too large. Maximum allowed size is ${maxFileSizeKb} KB.`,
      );
      return;
    }

    setIsLoading(true);
    try {
      const { width, height } = await getImageDimensions(file);
      if (width !== requiredWidth || height !== requiredHeight) {
        setLocalError(
          `Error. Unsupported dimensions. Upload an AVIF file sized ${requiredWidth}x${requiredHeight} px.`,
        );
        return;
      }

      const dataUrl = await toDataUrl(file);
      onChange(dataUrl);
      setPrefilledPreviewUrl(undefined);
      setSelectedFileSize(formatFileSizeKb(file.size));
    } catch (error) {
      setLocalError(
        error instanceof Error
          ? error.message
          : 'Unable to process selected file.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  // '' marks an explicit logo removal, so the prefetched preview must not
  // resurface after a remount (e.g. navigating back to this wizard step).
  const isLogoRemoved = formData === '';
  let previewSource: string | undefined;
  if (isPreviewableLogoValue(formData)) {
    previewSource = formData;
  } else if (!isLogoRemoved) {
    previewSource = prefilledPreviewUrl;
  }
  const hasLogo = Boolean(previewSource);

  useEffect(() => {
    setIsPreviewBroken(false);
  }, [previewSource]);

  // The existing logo is only shown as a preview (via the presigned URL) and is
  // intentionally never written into the form data. Submission contract:
  // - formData undefined -> keep the existing logo unchanged
  // - formData ''        -> remove the existing logo
  // - formData data URI  -> upload a new logo

  return (
    <Box>
      <Typography
        component="label"
        variant="caption"
        sx={{
          mb: 0.7,
          display: 'block',
          color: 'text.secondary',
          fontWeight: 400,
          fontSize: '0.75rem',
          lineHeight: 1.66,
          letterSpacing: 0,
          textTransform: 'none',
        }}
      >
        {schema.title ?? 'Agent logo'}
        {required ? '*' : ''}
      </Typography>

      <Paper
        variant="outlined"
        onDragOver={event => {
          event.preventDefault();
          setIsDragActive(true);
        }}
        onDragLeave={event => {
          event.preventDefault();
          setIsDragActive(false);
        }}
        onDrop={async event => {
          event.preventDefault();
          setIsDragActive(false);
          await validateAndSetFile(event.dataTransfer.files?.[0]);
        }}
        sx={{
          borderStyle: 'dashed',
          borderWidth: isDragActive ? 2.5 : 2,
          borderColor: theme =>
            theme.palette.mode === 'dark'
              ? AGENT_LOGO_DROPZONE_BORDER_DARK
              : AGENT_LOGO_DROPZONE_BORDER_LIGHT,
          p: 1.5,
          textAlign: 'center',
          backgroundColor: theme =>
            theme.palette.mode === 'dark'
              ? AGENT_LOGO_DROPZONE_BACKGROUND_DARK
              : AGENT_LOGO_DROPZONE_BACKGROUND_LIGHT,
        }}
      >
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={acceptedMimeTypes.join(',')}
          style={{ display: 'none' }}
          onChange={async event => {
            await validateAndSetFile(event.target.files?.[0]);
          }}
        />

        {!hasLogo ? (
          <Box sx={{ py: 1.5 }}>
            <Typography variant="body2" sx={{ fontSize: 13 }}>
              Drag and drop here, or{' '}
              <Button
                variant="text"
                size="small"
                disabled={isLoading}
                onClick={() => inputRef.current?.click()}
                sx={{
                  minWidth: 0,
                  p: 0,
                  fontSize: 13,
                  lineHeight: 'inherit',
                  textTransform: 'none',
                  verticalAlign: 'baseline',
                }}
              >
                browse
              </Button>
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary' }}
            >
              Supported file type: AVIF
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary' }}
            >
              File dimensions: {requiredWidth} x {requiredHeight} px
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary' }}
            >
              File size: {selectedFileSize ?? `max ${maxFileSizeKb} KB`}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 1.2 }}>
            {previewSource && !isPreviewBroken ? (
              <Box
                component="img"
                src={previewSource}
                alt="Agent logo preview"
                onError={() => setIsPreviewBroken(true)}
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 0.5,
                  objectFit: 'cover',
                  mx: 'auto',
                  display: 'block',
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 0.5,
                  backgroundColor: '#bdbdbd',
                  mx: 'auto',
                }}
              />
            )}
            <Button
              variant="text"
              size="small"
              disabled={isLoading}
              onClick={() => {
                onChange('');
                setPrefilledPreviewUrl(undefined);
                setSelectedFileSize(undefined);
                setLocalError(undefined);
                if (inputRef.current) {
                  inputRef.current.value = '';
                }
              }}
              sx={{
                color: 'error.main',
                textTransform: 'none',
                minWidth: 0,
                p: 0,
                mt: 0.8,
                fontSize: 11,
                '&:hover': { backgroundColor: 'transparent' },
              }}
            >
              Remove
            </Button>
          </Box>
        )}
      </Paper>

      <Box sx={{ mt: 0.7, minHeight: 20 }}>
        {schema.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ fontSize: 10, lineHeight: 1.35 }}
          >
            {schema.description}
          </Typography>
        )}
        {effectiveError && (
          <Typography
            variant="caption"
            color="error.main"
            display="block"
            sx={{ mt: 0.6, fontWeight: 500 }}
          >
            {effectiveError}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export const AgentLogoUploadFieldSchema: CustomFieldExtensionSchema = {
  returnValue: {
    type: ['string', 'null'],
  },
  uiOptions: {
    type: 'object',
    properties: {
      acceptedMimeTypes: {
        type: 'array',
        title: 'Accepted MIME Types',
        items: {
          type: 'string',
        },
      },
      requiredWidth: {
        type: 'number',
        title: 'Required image width',
      },
      requiredHeight: {
        type: 'number',
        title: 'Required image height',
      },
      maxFileSizeKb: {
        type: 'number',
        title: 'Maximum file size in KB',
      },
    },
  },
};
